import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ClientMarquee } from "@/components/ClientMarquee";
import { VALUES, STATS } from "@/data/content";
import { WHO } from "@/constants/testIds";

const OFFICE_IMG = "/team-photo-1.png";
const TEAM_IMG = "/team-photo-2.png";

export default function WhoWeAre() {
    return (
        <div data-testid={WHO.section}>
            {/* HERO */}
            <section className="container-x pt-24 pb-20">
                <p className="overline mb-8">Chapter 01 · Who we are</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.9] tracking-tighter max-w-[1200px]"
                        style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                    >
                        A senior team for{" "}
                        <span className="italic text-brand-primary">forex brokers and financial services in Africa.</span>
                    </h1>
                </Reveal>
                <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <Reveal className="lg:col-span-7" delay={120}>
                        <p className="text-xl text-brand-frost/90 leading-relaxed">
                            <span className="text-brand-primary">Adx Fusion</span> was started by people who spent their careers inside brokers and financial brands, not watching from an agency across town. We kept seeing agencies treat financial services like any other account, so we built the one we always wanted to hire.
                        </p>
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={240}>
                        <p className="text-brand-frost/80 leading-relaxed">
                            Today we work with forex brokers and financial brands of every size, from new entrants to Tier-1 names. We build the brand, win the accounts, and keep the clients trading.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Editorial Image Block */}
            <section className="border-y border-brand-primary/10">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7 relative bg-brand-bg">
                        <img
                            src={OFFICE_IMG}
                            alt="Adx Fusion team at an industry event booth"
                            className="w-full h-full aspect-[16/10] lg:aspect-auto lg:h-[640px] object-contain object-center"
                            loading="lazy"
                            decoding="async"
                        />
                    </Reveal>
                    <Reveal className="lg:col-span-5 p-8 sm:p-10 lg:p-16 bg-brand-surface flex flex-col justify-center" delay={120}>
                        <p className="overline mb-6">The studio</p>
                        <h2 className="font-display h-display-sm mb-6 text-brand-mist">
                            Remote-first.
                            <br />
                            Africa-focused.
                        </h2>
                        <p className="text-brand-frost/80 leading-relaxed">
                            We are a small, senior team. Strategists, buyers, designers and writers who work with you directly, not a chain of account managers passing your brand down the line. The people who pitch you are the people who run your account. We are based in Africa and work across the markets our clients sell into.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section className="py-24 lg:py-32">
                <div className="container-x">
                    <Reveal className="mb-20 max-w-4xl">
                        <p className="overline mb-6">Our principles</p>
                        <h2 className="font-display h-display-lg">
                            How we work,
                            <br />
                            <span className="italic text-brand-primary">non-negotiable.</span>
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-x-16 md:gap-y-20">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.slug} data-testid={WHO.valueCard(v.slug)} delay={i * 80} className="border-t border-brand-primary/20 pt-8">
                                <p className="overline mb-6">{v.kicker}</p>
                                <h3 className="font-display h-display-sm mb-4 text-brand-mist">{v.title}</h3>
                                <p className="text-brand-frost/80 leading-relaxed">{v.copy}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Editorial team image */}
            <section className="border-y border-brand-primary/10">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5 p-8 sm:p-10 lg:p-16 bg-brand-surface flex flex-col justify-center order-2 lg:order-1">
                        <p className="overline mb-6">Built on industry experience</p>
                        <h2 className="font-display h-display-sm mb-6 text-brand-mist">
                            Senior, specialist, in-house.
                        </h2>
                        <p className="text-brand-frost/80 leading-relaxed mb-10">
                            The numbers below reflect the combined experience of our founding team across forex brokerages and financial services firms, before <span className="text-brand-primary">Adx Fusion</span>. We bring that industry lens to every engagement.
                        </p>
                        <div className="grid grid-cols-2 gap-6">
                            {STATS.map((s) => (
                                <div key={s.label} className="border-t border-brand-primary/20 pt-4">
                                    <p className="font-display text-3xl text-brand-mist tracking-tight">{s.value}</p>
                                    <p className="text-xs text-brand-primary/70 uppercase tracking-wider mt-1">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal className="lg:col-span-7 order-1 lg:order-2 bg-brand-bg" delay={120}>
                        <img
                            src={TEAM_IMG}
                            alt="Adx Fusion team at the ADXFUSION Network Lounge"
                            className="w-full h-full aspect-[16/10] lg:aspect-auto lg:h-[640px] object-contain object-center"
                            loading="lazy"
                            decoding="async"
                        />
                    </Reveal>
                </div>
            </section>

            {/* Clients */}
            <section className="py-16 bg-brand-bg">
                <div className="container-x mb-8">
                    <p className="overline">Brands we&apos;ve worked with</p>
                </div>
                <ClientMarquee outline />
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x">
                    <Reveal className="max-w-4xl">
                        <h2 className="font-display h-display-lg">
                            Want to see how we&apos;d
                            <br />
                            <span className="italic text-brand-primary">approach your firm?</span>
                        </h2>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" className="btn-primary">
                                Book a working session <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/snapshot" className="btn-secondary">
                                See snapshots
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
